import { Button, Flex, Heading, Icon, Image, Text } from "@chakra-ui/react"
import { BlockImageUploadEntityHydrated } from "@jackfruit/common"
import { withPrefix } from "gatsby"
import React, { useCallback } from "react"
import { useDropzone } from "react-dropzone"
import { useTranslation } from "react-i18next"
import { FiUpload } from "react-icons/fi"
import Wrapper from "~/components/Wrapper"
import { UPLOAD_ACCEPTED_FILE_TYPES } from "~/data/const"
import { useCurrentPageId } from "~/hooks/useCurrentPageId"
import { useNextActionableBlock } from "~/hooks/useNextActionableBlock"
import { usePageSession } from "~/hooks/usePageSession"
import { useProcessActions } from "~/hooks/useProcessActions"
import { scrollTo } from "~/services/Utils"
import CloudProviderHandler from "../cloud-provider/CloudProviderHandler"
import "./ImageUploadBlock.css"

interface Props {
  config: BlockImageUploadEntityHydrated
}

const ImageUploadBlockV2: React.FC<Props> = () => {
  const { t } = useTranslation()
  const pageId = useCurrentPageId()
  const process = useProcessActions()
  const { nextBlockType } = useNextActionableBlock("image-upload")
  const {
    pageSession: { isReady },
  } = usePageSession()

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (isReady && acceptedFiles.length > 0) {
        const firstImage = acceptedFiles[0]
        process.createUpload({
          pageId,
          file: firstImage,
          scrollTo,
          nextBlockName: nextBlockType,
        })

        const remainingImages = acceptedFiles.slice(1)
        remainingImages.forEach((file: File) =>
          process.createUpload({
            pageId,
            file,
          })
        )
      }
    },
    [isReady, nextBlockType, pageId, process]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: UPLOAD_ACCEPTED_FILE_TYPES,
  })

  const headingBlock = (
    <>
      <Heading as="h3" mb={2} fontSize="2xl">
        {t("components.blocks.ImageUploadBlock.UploadHeading")}
      </Heading>
      <Text mb={8}>
        {t("components.blocks.ImageUploadBlock.UploadDescription")}
      </Text>
    </>
  )

  const desktopUpload = (
    <Flex
      flex={1}
      borderWidth={2}
      borderRadius="lg"
      borderStyle="dashed"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      gap={2}
      p={8}
      id="image-upload-field"
      data-testid="image-upload-field"
      className="ImageUpload_svg"
      _hover={{ backgroundColor: "blackAlpha.50" }}
      {...getRootProps()}
    >
      <input {...getInputProps()} />
      <Icon as={FiUpload} boxSize="10" />
      <Heading as="h3" fontSize="xl">
        Select Photos
      </Heading>
      <Text textAlign="center">
        {t("components.blocks.ImageUploadBlock.UploadFromDevice")}
      </Text>
    </Flex>
  )

  const verizonCloudUpload = (
    <CloudProviderHandler isEnabled={isReady} cloudProviderType="verizon-cloud">
      <Flex
        height="332px"
        as={Button}
        backgroundColor="transparent"
        flex={1}
        borderWidth={1}
        p={8}
        borderRadius="lg"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        _hover={{ backgroundColor: "blackAlpha.50" }}
        cursor="pointer"
      >
        <Image
          src={withPrefix("/images/cloud-providers/vnz/ic_verizon-v.svg")}
          height="64px"
          width="64px"
          mb={3}
        />
        <Heading as="h3" fontSize="xl">
          Verizon Cloud
        </Heading>

        <Text>Free Shipping!</Text>
      </Flex>
    </CloudProviderHandler>
  )

  return (
    <Wrapper
      id="p-upload" // GTM: upload visible
      hasSeparator={true}
      paddingTop={16}
      borderTopColor="gray.200"
      borderTopWidth={1}
    >
      <Flex
        display={{ base: "flex", md: "none" }}
        flexDirection="column"
        gap={4}
        textAlign="center"
      >
        {headingBlock}
        {desktopUpload}
        {verizonCloudUpload}
      </Flex>
      <Flex
        display={{ base: "none", md: "flex" }}
        direction="column"
        alignItems="center"
        textAlign="center"
      >
        {headingBlock}
        <Flex flex={1} gap={4} maxWidth="1024px" width="full">
          <Flex flex={1}>{desktopUpload}</Flex>
          <Flex flex={1}>{verizonCloudUpload}</Flex>
        </Flex>
      </Flex>
    </Wrapper>
  )
}

export default ImageUploadBlockV2
